import { useEffect } from "react";
import BiloxiHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "Can I get a same-day passport in Biloxi?", a: "Yes — same-day emergency passport service is available for Biloxi travelers with urgent departures. We are a registered State Department courier and can process your application as fast as 24 hours. Call us immediately at 877-253-0084 to verify eligibility and schedule your emergency submission." },
  { q: "Where do I go for a passport photo in Biloxi?", a: "Passport photos are available at CVS, Walgreens, and the Biloxi Post Office on Lamey Street. For expedited processing, you can also mail your photo directly to us with your application package." },
  { q: "How long does routine passport processing take from Biloxi?", a: "Routine processing from the Biloxi post office takes 10–13 weeks. Expedited service at the post office takes 7–9 weeks. Our private courier service can deliver your passport in as little as 24 hours to 2 weeks depending on urgency." },
  { q: "Can Keesler AFB personnel get emergency passport help?", a: "Absolutely. We specialize in military PCS and deployment orders. Keesler AFB families and service members can use our emergency service with deployment documentation. We understand the urgency of military orders and prioritize these applications." },
  { q: "Do you serve Gulfport, D'Iberville, and Ocean Springs?", a: "Yes, we serve the entire Mississippi Gulf Coast region including Gulfport, Long Beach, Pass Christian, D'Iberville, Ocean Springs, St. Martin, and all surrounding coastal communities. All residents use the same expedited process with no office visit required." },
  { q: "What cruise ports do Biloxi travelers typically use?", a: "Biloxi residents typically depart from the Port of Gulfport, New Orleans (90 minutes west), or Mobile (1 hour east). Our expedited service ensures you have your passport well before your cruise departure date, even for last-minute bookings." },
  { q: "Can I renew my passport if it expired 3 years ago?", a: "Yes, you can renew by mail if your previous passport was issued when you were 16 or older and within the last 15 years. Our renewal service includes checking your eligibility, preparing the DS-82 form, and submitting via our expedited courier channel." },
  { q: "What forms of payment do you accept?", a: "We accept all major credit cards, debit cards, and PayPal. The U.S. Department of State portion of your fee must be paid by check or money order, which we guide you through during the application process." },
];

const services = [
  { icon: "ri-flashlight-line", title: "Emergency Passport", desc: "Same-day to 24-hour processing for urgent departures. Ideal for Keesler AFB PCS orders, cruise departures, and last-minute business trips." },
  { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for expired or expiring passports. DS-82 form assistance with photo guidance and expedited courier submission." },
  { icon: "ri-passport-line", title: "New Passport", desc: "First-time application support with step-by-step guidance. We help with the DS-11 form, acceptance agent appointment, and document preparation." },
  { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "DS-64 replacement form guidance and emergency reissue. We help you navigate the police report requirement and get a new passport fast." },
  { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 require both parents' consent. We guide you through the parental consent forms, acceptance clerk process, and photo requirements." },
  { icon: "ri-edit-line", title: "Name Change", desc: "Marriage, divorce, or legal name change updates. DS-5504 or DS-82 form support with required documentation guidance." },
  { icon: "ri-stack-line", title: "Second Passport", desc: "For frequent travelers needing concurrent passports. Common for business travelers visiting restricted countries while maintaining primary passport validity." },
  { icon: "ri-first-aid-kit-line", title: "Damaged Passport", desc: "Water-damaged, torn, or mutilated passport replacement. We assess the damage level and recommend the best replacement path." },
];

const processingSteps = [
  { icon: "ri-calendar-check-line", title: "Emergency — 24 Hours", desc: "Same-day processing for life-or-death emergencies and urgent military PCS orders. Requires proof of immediate travel.", price: "Contact us" },
  { icon: "ri-rocket-line", title: "Rush — 3-5 Business Days", desc: "Fast turnaround for urgent business trips, cruise departures, and last-minute vacation bookings. Most popular for Keesler AFB families.", price: "From $399" },
  { icon: "ri-flight-takeoff-line", title: "Priority — 2 Weeks", desc: "Standard expedited service for planned international travel. Ideal for study abroad programs and advance vacation planning.", price: "From $299" },
  { icon: "ri-time-line", title: "Standard — 6-9 Weeks", desc: "Regular processing with professional application review. We ensure your forms are correct before submission to avoid delays.", price: "From $199" },
];

const testimonials = [
  { name: "Michael S.", role: "Keesler AFB, MS", text: "Got my PCS orders to Germany with 10 days notice. Fast Passport Center had my passport in 3 days. Saved my assignment.", stars: 5 },
  { name: "Jennifer R.", role: "Biloxi Beach, MS", text: "Cruise to the Bahamas in 2 weeks and my passport was expired. They got it renewed in 5 days. Absolutely amazing service.", stars: 5 },
  { name: "David K.", role: "D'Iberville, MS", text: "Casino industry exec with sudden Macau trip. Emergency service was flawless. Will recommend to all my colleagues.", stars: 5 },
  { name: "Lisa M.", role: "Ocean Springs, MS", text: "First-time passport for my daughter's study abroad. They walked me through every step. So grateful for the personal attention.", stars: 5 },
];

const facilities = [
  { name: "Biloxi Main Post Office", address: "1350 Lamey St, Biloxi, MS 39531", phone: "(228) 432-1575", hours: "Mon–Fri 9:00 AM–4:00 PM", notes: "Passport acceptance by appointment. Photo services available." },
  { name: "Gulfport Main Post Office", address: "11110 Highway 49, Gulfport, MS 39503", phone: "(228) 864-1620", hours: "Mon–Fri 9:00 AM–4:00 PM", notes: "Passport acceptance by appointment." },
  { name: "D'Iberville Post Office", address: "10333 Auto Mall Pkwy, D'Iberville, MS 39540", phone: "(228) 392-9024", hours: "Mon–Fri 9:00 AM–4:00 PM", notes: "Limited passport hours. Call ahead." },
  { name: "Ocean Springs Post Office", address: "1580 Government St, Ocean Springs, MS 39564", phone: "(228) 875-1941", hours: "Mon–Fri 9:00 AM–4:00 PM", notes: "Passport acceptance by appointment." },
  { name: "Pass Christian Post Office", address: "101 N Beach Blvd, Pass Christian, MS 39571", phone: "(228) 452-2315", hours: "Mon–Fri 9:00 AM–4:00 PM", notes: "Passport acceptance by appointment." },
];

const comparisonRows = [
  { feature: "Processing Speed", us: "As fast as 24 hours", them: "7–9 weeks at post office" },
  { feature: "Office Visit Required", us: "No — ship documents to us", them: "Yes — in-person appointment" },
  { feature: "Photo Guidance", us: "Detailed instructions included", them: "Self-service" },
  { feature: "Form Review", us: "Expert review before submission", them: "Clerk basic check" },
  { feature: "Military PCS Support", us: "Specialized documentation help", them: "Standard process" },
  { feature: "Status Tracking", us: "Real-time updates via phone/email", them: "Limited online tracking" },
  { feature: "Experience with Complex Cases", us: "20+ years handling edge cases", them: "Varies by location" },
  { feature: "Price", us: "Best price guarantee", them: "Government fee + $60 expedite" },
  { feature: "Cruise & Casino Industry Experience", us: "Specialized for Gulf Coast travelers", them: "Generic service" },
];

const mistakes = [
  { icon: "ri-image-line", title: "Wrong Photo Size", desc: "Many Biloxi applicants submit photos that do not meet the 2x2 inch, white-background requirement. We provide exact specifications and can recommend local photo services that guarantee acceptance." },
  { icon: "ri-file-text-line", title: "Missing Travel Documentation", desc: "For emergency service, you need proof of imminent travel. We help you gather the correct flight itineraries, cruise confirmations, or military orders to qualify for expedited processing." },
  { icon: "ri-money-dollar-circle-line", title: "Incorrect Payment", desc: "The State Department requires a separate check or money order. Many applicants forget this or use the wrong amount. We provide a payment checklist to ensure your application is not rejected." },
  { icon: "ri-parent-line", title: "Missing Parental Consent", desc: "For minors under 16, both parents must appear or provide notarized consent. We walk you through the DS-3053 form to avoid this common rejection reason." },
  { icon: "ri-calendar-line", title: "Waiting Too Long", desc: "Gulf Coast residents often wait until the week before a cruise or deployment. Our reminder system helps you start the process early enough to avoid emergency fees." },
];

const howItWorks = [
  { step: "1", title: "Call or Apply Online", desc: "Call 877-253-0084 or start online. Tell us your travel date and urgency. We'll recommend the right processing speed and answer your Biloxi-specific questions." },
  { step: "2", title: "Gather Your Documents", desc: "We send you a personalized checklist with exact requirements. For Biloxi residents, this includes guidance on local acceptance facilities and photo services." },
  { step: "3", title: "Visit Acceptance Agent", desc: "For new passports, visit a local acceptance clerk (post office or clerk of court). We tell you exactly which Biloxi-area facilities accept walk-ins versus appointments." },
  { step: "4", title: "Ship to Fast Passport Center", desc: "Send your sealed envelope to our registered courier facility. We verify everything is correct before hand-delivering to the State Department." },
  { step: "5", title: "Receive Your Passport", desc: "Your passport arrives via secure delivery. We track it every step and notify you the moment it ships." },
];

const travelTips = [
  { icon: "ri-ship-line", title: "Cruise Departures", desc: "Biloxi residents often depart from the Port of Gulfport, New Orleans, or Mobile. Book your passport at least 6 months before cruising. Many Caribbean cruises require passports even for closed-loop itineraries." },
  { icon: "ri-building-line", title: "Casino & Business Travel", desc: "Gulf Coast casino industry professionals frequently travel to Macau, Singapore, and the Philippines. Our business travel documentation support ensures you meet visa requirements alongside your passport." },
  { icon: "ri-sun-line", title: "Spring & Summer Rush", desc: "Biloxi experiences peak demand from March through August as families book cruises and beach vacations. Apply at least 8 weeks ahead during these months." },
  { icon: "ri-windy-line", title: "Hurricane Season Backup", desc: "Gulf Coast residents may need to evacuate internationally during hurricane season. Having a valid passport ensures you can travel anywhere if needed." },
];

const destinations = [
  { name: "Caribbean Cruises", desc: "Biloxi residents' #1 choice — Bahamas, Jamaica, Cozumel, and the Virgin Islands. Most cruises require 6 months passport validity.", popular: true },
  { name: "Mexico Beach Resorts", desc: "Cancun, Riviera Maya, and Los Cabos are popular for Gulf Coast families. Passport required for air travel; enhanced driver's license works for land crossings only.", popular: true },
  { name: "Europe", desc: "Paris, London, Rome, and Barcelona for casino industry conferences and luxury vacations. Schengen Zone requires 3 months beyond planned departure.", popular: false },
  { name: "Macau & Singapore", desc: "Business travel destinations for Gulf Coast casino executives. Visa requirements vary — we help you prepare both passport and visa documentation.", popular: false },
  { name: "Japan", desc: "Keesler AFB personnel often travel to Japan for military coordination and cultural exchange. Japan requires 6 months passport validity.", popular: false },
  { name: "Canada & Alaska", desc: "Popular for summer cruises departing from Seattle or Vancouver. Passport required for all international cruise segments.", popular: false },
];

export default function BiloxiPage() {
  useEffect(() => {
    document.title = "Emergency Passport Biloxi, MS — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/biloxi/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Biloxi, MS — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Keesler AFB, Gulf Coast casino industry, and cruise passengers.");
    setMeta("geo.placename", "Biloxi, Mississippi");
    setMeta("geo.region", "US-MS");
    setMeta("geo.position", "30.3960;-88.8853");
    setMeta("ICBM", "30.3960, -88.8853");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Mississippi", item: "https://fastpassportcenter.com/mississippi/" },
          { "@type": "ListItem", position: 3, name: "Biloxi Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Biloxi — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Biloxi and Mississippi Gulf Coast travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Biloxi",
          containedInPlace: {
            "@type": "State",
            name: "Mississippi",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Biloxi",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <BiloxiHeroSection />

      {/* Sticky Nav */}
      <nav className="sticky top-[70px] z-40 bg-[#1A365D] border-b border-[#1A365D]/80">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-6 overflow-x-auto">
          {["Processing", "Services", "Local Area", "Destinations", "FAQ", "How It Works"].map((item) => (
            <a key={item} href={`#${item.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-white/90 hover:text-[#F6C344] font-medium whitespace-nowrap transition-colors">
              {item}
            </a>
          ))}
          <a href="https://fastpassportcenter.com/checkout/" className="ml-auto bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-4 py-2 rounded-md whitespace-nowrap transition-all">
            Get Started
          </a>
        </div>
      </nav>

      {/* Processing Time Section */}
      <section id="processing" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Biloxi Passport Processing Times</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Choose the speed that fits your travel schedule. From same-day emergency to standard expedited, we have a solution for every Biloxi traveler.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processingSteps.map((step, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${step.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-lg mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{step.desc}</p>
                <p className="text-[#F6C344] font-bold text-sm">{step.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Biloxi &amp; Gulf Coast</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Keesler AFB families, casino professionals, cruise passengers, and every Biloxi traveler.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 hover:bg-[#EEF2F8] transition-all">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white mb-4">
                  <i className={`${s.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area Section */}
      <section id="local-area" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area Guide</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Biloxi &amp; Surrounding Areas</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <p className="text-gray-600 text-base leading-relaxed mb-4">
                Biloxi sits at the heart of the Mississippi Gulf Coast, a vibrant region where military service, casino tourism, and coastal living converge. Home to Keesler Air Force Base with over 4,000 active-duty personnel and their families, Biloxi sees a constant flow of military PCS orders requiring urgent passport processing. The base's training missions and international partnerships mean service members and their dependents frequently need expedited documentation for overseas assignments.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-4">
                Beyond the military presence, Biloxi is the entertainment capital of the Gulf Coast. The Beau Rivage, Hard Rock, and Golden Nugget casinos employ thousands of hospitality professionals who travel internationally for industry conferences, vendor meetings, and guest acquisition trips. The casino industry's global reach means Biloxi executives regularly need visas and passports for destinations like Macau, Singapore, and the Philippines — destinations we are well-versed in supporting.
              </p>
              <p className="text-gray-600 text-base leading-relaxed">
                The cruise industry is another major driver of passport demand. While Biloxi itself does not host major cruise terminals, residents are just 90 minutes from the Port of New Orleans and 1 hour from Mobile's cruise port. Carnival, Royal Caribbean, and Norwegian cruise lines all depart from these nearby ports, and Biloxi families frequently book Caribbean cruises to the Bahamas, Jamaica, and Cozumel. We remind all cruise passengers that even "closed-loop" cruises may require passports for emergency medical evacuations or missed port reboardings.
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-base leading-relaxed mb-4">
                The surrounding communities of Gulfport, D'Iberville, Ocean Springs, Pass Christian, Long Beach, and St. Martin all rely on Biloxi for their closest passport acceptance facilities. The Biloxi Main Post Office on Lamey Street and the Gulfport Main Post Office on Highway 49 are the primary acceptance agents for the entire coastal region. During spring break and summer vacation seasons, these facilities experience significant demand, and appointments can book weeks in advance.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-4">
                For residents in the more rural eastern communities like Vancleave and Escatawpa, the Ocean Springs Post Office on Government Street offers the most convenient acceptance service. West Biloxi residents and those in Pass Christian can also use the Pass Christian Post Office on North Beach Boulevard. We provide location-specific guidance to every client based on their address and urgency level.
              </p>
              <p className="text-gray-600 text-base leading-relaxed">
                The climate and hurricane season also play a role in passport urgency. Gulf Coast residents sometimes need to evacuate internationally during severe hurricane threats. Having a current passport ensures you can travel to any safe destination, not just domestic evacuation sites. We recommend all Biloxi families maintain valid passports for every household member, including infants, as a standard disaster preparedness measure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Timing Section */}
      <section id="travel-timing" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Best Times to Apply from Biloxi</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelTips.map((tip, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${tip.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{tip.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section id="destinations" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Biloxi Travelers Go</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {destinations.map((dest, i) => (
              <div key={i} className={`bg-white rounded-lg p-6 border ${dest.popular ? "border-[#F6C344]" : "border-gray-200"} hover:border-[#F6C344] transition-all`}>
                {dest.popular && <span className="inline-block bg-[#F6C344] text-[#1A365D] text-xs font-bold px-2.5 py-1 rounded-full mb-3">Most Popular</span>}
                <h3 className="font-bold text-[#1A365D] text-lg mb-2">{dest.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{dest.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Biloxi Travelers Say</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <p className="font-bold text-[#1A365D] text-sm">{t.name}</p>
                <p className="text-gray-400 text-xs">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Facilities Section */}
      <section id="acceptance-facilities" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Biloxi</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">For new passport applications, you must visit an acceptance agent first. These are the closest facilities to Biloxi.</p>
          </div>
          <div className="space-y-4">
            {facilities.map((f, i) => (
              <div key={i} className="bg-white rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-all">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-base mb-1">{f.name}</h3>
                    <p className="text-gray-500 text-sm">{f.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{f.hours} &middot; {f.phone}</p>
                  </div>
                  <p className="text-gray-500 text-sm md:text-right md:max-w-xs">{f.notes}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="comparison" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Post Office</h2>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-3 bg-[#1A365D] text-white text-sm font-bold p-4">
              <div>Feature</div>
              <div className="text-center">Fast Passport Center</div>
              <div className="text-center">USPS / Post Office</div>
            </div>
            {comparisonRows.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 p-4 text-sm ${i % 2 === 0 ? "bg-[#F7F8FA]" : "bg-white"}`}>
                <div className="font-medium text-[#1A365D]">{row.feature}</div>
                <div className="text-center text-gray-600">{row.us}</div>
                <div className="text-center text-gray-400">{row.them}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mistakes Section */}
      <section id="mistakes" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Errors</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Mistakes Biloxi Applicants Make</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mistakes.map((m, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-red-200 transition-all">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mb-4">
                  <i className={`${m.icon} text-red-500 text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{m.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Simple Process</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {howItWorks.map((step, i) => (
              <div key={i} className="relative">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-lg mb-4">{step.step}</div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Biloxi Passport Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer">
                    <span className="font-bold text-[#1A365D] text-base">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-gray-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Helpful Resources Section */}
      <section id="helpful-resources" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Links for Biloxi Travelers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 hover:bg-[#EEF2F8] transition-all">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">U.S. State Department Passport Hub</h3>
              <p className="text-gray-500 text-xs">Official passport information, forms, and fee schedules.</p>
            </a>
            <a href="https://www.keesler.af.mil/" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 hover:bg-[#EEF2F8] transition-all">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Keesler Air Force Base</h3>
              <p className="text-gray-500 text-xs">Military personnel resources and PCS information.</p>
            </a>
            <a href="https://www.biloxi.ms.us/" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 hover:bg-[#EEF2F8] transition-all">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">City of Biloxi</h3>
              <p className="text-gray-500 text-xs">Local government services and community information.</p>
            </a>
            <a href="https://www.carnival.com/" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 hover:bg-[#EEF2F8] transition-all">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Carnival Cruise Lines</h3>
              <p className="text-gray-500 text-xs">Cruise departures from New Orleans and Mobile — passport requirements.</p>
            </a>
            <a href="https://www.cdc.gov/travel" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 hover:bg-[#EEF2F8] transition-all">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">CDC Travel Health</h3>
              <p className="text-gray-500 text-xs">Vaccination and health requirements for international destinations.</p>
            </a>
            <a href="https://www.tsa.gov/precheck" target="_blank" rel="noopener noreferrer" className="bg-[#F7F8FA] rounded-lg p-5 hover:bg-[#EEF2F8] transition-all">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">TSA PreCheck</h3>
              <p className="text-gray-500 text-xs">Expedited airport security — pair with your passport for smoother travel.</p>
            </a>
          </div>
        </div>
      </section>

      <SEOCrossLinks currentRoute="/cities/biloxi" state="MS" />

      {/* Final CTA */}
      <section className="bg-[#1A365D] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Passport?</h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">Biloxi travelers trust Fast Passport Center for emergency, rush, and standard expedited passport processing. Call now or start online — your passport is closer than you think.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
              Start My Application
            </a>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 text-white hover:text-[#F6C344] font-bold text-base transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i>877-253-0084
            </a>
          </div>
          <p className="text-white/50 text-sm mt-6">Serving Biloxi, Gulfport, D'Iberville, Ocean Springs, Pass Christian, Long Beach, and all Mississippi Gulf Coast communities.</p>
        </div>
      </section>
    </div>
  );
}